// CaseTableDockerSideBar.jsx
// Display the sidebar of the case table with the slide folder and case information as dockable tabs

// Copyright HS Analysis GmbH, 2023
// Authors: Jakob Burghardt, Viktor Eberhardt

// Framework imports
import React from "react";
import PropTypes from "prop-types";

// MUI imports
import { makeStyles } from "@mui/styles";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";

// Icon imports
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import HSAIcon from "../../common/utils/HSAIcon";

// External imports
import DockLayout from "rc-dock";

// HSA imports
import FilePreviewList from "../../fileViewer/components/FilePreviewList";
import CaseInformation from "./CaseInformation";
import { SortByType } from "../../fileViewer/FileViewer";
import FileFormatUtils from "../../common/utils/FileFormatUtils";
import FilePreviewEditDialog from "../../fileViewer/components/FilePreviewEditDialog";

export const CaseContext = React.createContext();
export default function CaseTableDockSideBar(props) {
  const { selCase } = props;
  const classes = useStyles();
  const [anchorElFolder, setAnchorElFolder] = React.useState(null);
  const [anchorElPreview, setAnchorElPreview] = React.useState(null);
  const [dockKey, setDockKey] = React.useState(0);
  const [caseFileSortReverse, setCaseFileSortReverse] = React.useState(() => {
    try {
      const raw = localStorage.getItem("hsaFilePreviewSortReverse");
      return raw === "true";
    } catch {
      return false;
    }
  });
  const [filePreviewSortBy, setFilePreviewSortBy] = React.useState(() => {
    try {
      return localStorage.getItem("hsaFilePreviewSortBy") || SortByType.Name;
    } catch {
      return SortByType.Name;
    }
  });
  const [sceneDataList, setSceneDataList] = React.useState([]);
  const [showAdditionalName, setShowAdditionalName] = React.useState(
    (localStorage.getItem("hsaFileViewerShowFileName") ?? "true") === "true"
  );
  const [showComments, setShowComments] = React.useState(
    (localStorage.getItem("hsaFileViewerShowComments") ?? "true") === "true"
  );
  const [showLabel, setShowLabel] = React.useState(
    (localStorage.getItem("hsaFileViewerShowLabel") ?? "true") === "true"
  );
  const [
    useAdditionalNameInsteadOfFilename,
    setUseAdditionalNameInsteadOfFilename,
  ] = React.useState(
    (localStorage.getItem("hsaFileViewerUseDisplayName") ?? "false") === "true"
  );
  // When enabled, clicking a preview opens it in a new browser window/tab
  const [openInNewWindow, setOpenInNewWindow] = React.useState(() => {
    try {
      const raw = localStorage.getItem("hsaFilePreviewOpenInNewWindow");
      return raw === "true";
    } catch {
      return false;
    }
  });
  const [filePreviewDialogOpen, setFilePreviewDialogOpen] =
    React.useState(false);

  const activeFileId = React.useMemo(() => {
    // return the id of the scene that has the newest lastOpenedAt date
    if (!sceneDataList || sceneDataList.length === 0) return null;
    const sorted = [...sceneDataList].sort((a, b) => {
      return new Date(b.lastOpenedAt) - new Date(a.lastOpenedAt);
    });
    return sorted[0]?.id || null;
  }, [sceneDataList]);

  const [layout, setLayout] = React.useState({
    dockbox: {
      mode: "horizontal",
      children: [
        {
          tabs: [{ id: "slideFolderTab" }],
        },
        {
          tabs: [{ id: "caseInformationTab" }],
        },
      ],
    },
  });

  React.useEffect(() => {
    if (!selCase || !selCase.Files || selCase.Files.length === 0) return;
    const sortedList = FileFormatUtils.sortEntitiesByTypeAndName(
      sceneDataList,
      filePreviewSortBy,
      false,
      caseFileSortReverse
    );
    setSceneDataList(sortedList);
  }, [filePreviewSortBy, caseFileSortReverse]);

  /**
   * Triggers a re-render of the layout by updating the dock key state.
   */
  const rerenderLayout = () => {
    setDockKey(dockKey + 1);
  };

  /**
   * Handles the event when the folder menu is opened.
   *
   * @param {Object} event - The event object triggered by the user action.
   */
  const onOpenFolderMenu = (event) => {
    setAnchorElFolder(event.currentTarget);
  };

  const onOpenPreviewMenu = (event) => {
    setAnchorElPreview(event.currentTarget);
  };

  React.useEffect(() => {
    rerenderLayout();
  }, [selCase]);

  // Derive pathList for previews from the selected case
  const casePathList = React.useMemo(() => {
    const files = Array.isArray(selCase?.Files) ? selCase.Files : [];
    return files.map((path) => ({ path, type: "file" }));
  }, [selCase]);

  let groups = {
    custom: {
      floatable: false,
      maximizable: false,
    },
  };

  /**
   * An object representing the slide folder tab configuration.
   *
   * @type {Object}
   * @property {JSX.Element} title - The title element of the tab, which includes a folder label and an icon button for opening a menu.
   * @property {JSX.Element} content - The content element of the tab, which renders the `SlideFolder` component with context values.
   * @property {string} group - The group to which this tab belongs, set to "custom".
   */
  let slideFolderTab = {
    title: (
      <div className={classes.previewTabTitle}>
        <div>PREVIEW</div>
        <Tooltip disableInteractive title="Sort by">
          <IconButton onClick={onOpenFolderMenu}>
            <HSAIcon icon="SortIcon" size={28} />
          </IconButton>
        </Tooltip>
        <Tooltip disableInteractive title="Preview options">
          <IconButton onClick={onOpenPreviewMenu}>
            <HSAIcon icon="MoreVertIcon" size={28} />
          </IconButton>
        </Tooltip>
      </div>
    ),
    content: <FilePreviewList />,

    group: "custom",
  };

  let caseInformationTab = {
    title: (
      <div className={classes.previewTabTitle}>
        <div>CASE INFORMATION</div>
        <span></span>
      </div>
    ),
    content: <CaseInformation />,
    group: "custom",
  };

  const loadTab = (data) => {
    let { id } = data;
    switch (id) {
      case "slideFolderTab":
        return { ...slideFolderTab, id };
      case "caseInformationTab":
        return { ...caseInformationTab, id };
    }
  };

  const onLayoutChange = (newLayout) => {
    let newChilds = newLayout.dockbox.children;
    let oldChilds = layout.dockbox.children;
    if (JSON.stringify(newChilds) !== JSON.stringify(oldChilds)) {
      setLayout(newLayout);
      // props.persistentStorage.save("SidebarLayout", newLayout);
    }
  };

  /**
   * Closes the menu by setting the anchor element for the folder to null.
   */
  const handleMenuClose = () => {
    setAnchorElFolder(null);
  };

  /**
   * Closes the preview menu by resetting the anchor element to null.
   * Typically used to hide or dismiss a popover or menu component.
   */
  const handlePreviewMenuClose = () => {
    setAnchorElPreview(null);
  };

  /**
   * Handles the sorting logic for file previews in the case table sidebar.
   * If the selected sort option is the same as the current one, toggles the sort order.
   * Otherwise, updates the sort option to the newly selected one.
   * Closes the menu and triggers a layout re-render after sorting.
   *
   * @param {string} sortBy - The field by which to sort the file previews.
   */
  const onChangeFileSortBy = (sortBy) => {
    if (filePreviewSortBy === sortBy) {
      const nextReverse = !caseFileSortReverse;
      setCaseFileSortReverse(nextReverse);
      try {
        localStorage.setItem("hsaFilePreviewSortReverse", String(nextReverse));
      } catch {
        // ignore persistence error
      }
    } else {
      setFilePreviewSortBy(sortBy);
      try {
        localStorage.setItem("hsaFilePreviewSortBy", sortBy);
        // keep current reverse setting as-is
        localStorage.setItem(
          "hsaFilePreviewSortReverse",
          String(caseFileSortReverse)
        );
      } catch {
        // ignore persistence error
      }
    }
    handleMenuClose();
    rerenderLayout();
  };

  /**
   * Toggles the visibility of the additional name field in the UI.
   * Updates the `showAdditionalName` state to its opposite value.
   */
  const toggleAdditionalName = () => {
    const next = !showAdditionalName;
    setShowAdditionalName(next);
    try {
      localStorage.setItem("hsaFileViewerShowFileName", String(next));
    } catch (e) {
      // ignore persistence error
    }
  };

  /**
   * Toggles the visibility of the comments section in the UI.
   * Updates the `showComments` state to its opposite value.
   */
  const toggleShowComments = () => {
    const next = !showComments;
    setShowComments(next);
    try {
      localStorage.setItem("hsaFileViewerShowComments", String(next));
    } catch (e) {
      // ignore persistence error
    }
  };

  /**
   * Toggles the visibility of the slide label in the preview.
   */
  const toggleShowLabel = () => {
    const next = !showLabel;
    setShowLabel(next);
    try {
      localStorage.setItem("hsaFileViewerShowLabel", String(next));
    } catch (e) {
      // ignore persistence error
    }
  };

  /**
   * Toggles whether the center text uses Additional Name instead of Filename.
   */
  const toggleUseAdditionalNameInsteadOfFilename = () => {
    const next = !useAdditionalNameInsteadOfFilename;
    setUseAdditionalNameInsteadOfFilename(next);
    try {
      localStorage.setItem("hsaFileViewerUseDisplayName", String(next));
    } catch (e) {
      // ignore persistence error
    }
  };

  /**
   * Toggle whether preview clicks open in a new window instead of the current one.
   * Persist the choice to localStorage for convenience.
   */
  const toggleOpenInNewWindow = () => {
    setOpenInNewWindow((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("hsaFilePreviewOpenInNewWindow", String(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  /**
   * Opens the file preview and edit dialog.
   * Sets the dialog state to open and closes the preview menu.
   */
  const openFilePreviewEditDialog = () => {
    setFilePreviewDialogOpen(true);
    handlePreviewMenuClose();
  };

  return (
    <CaseContext.Provider
      value={{
        activeFileId,
        filePreviewSortBy,
        filePreviewSortReverse: caseFileSortReverse,
        // Provide the list of file paths so FilePreviewList can render placeholders and lazy-load scenes
        pathList: casePathList,
        sceneDataList,
        selCase,
        setActiveFileId: () => {},
        setActiveFilePath: () => {},
        setPathList: () => {},
        setSceneDataList,
        setShowAdditionalName: (v) => {
          setShowAdditionalName(v);
          try {
            localStorage.setItem("hsaFileViewerShowFileName", String(v));
          } catch (e) {
            // ignore persistence error
          }
        },
        setShowComments: (v) => {
          setShowComments(v);
          try {
            localStorage.setItem("hsaFileViewerShowComments", String(v));
          } catch (e) {
            // ignore persistence error
          }
        },
        setShowLabel: (v) => {
          setShowLabel(v);
          try {
            localStorage.setItem("hsaFileViewerShowLabel", String(v));
          } catch (e) {
            // ignore persistence error
          }
        },
        setUseAdditionalNameInsteadOfFilename: (v) => {
          setUseAdditionalNameInsteadOfFilename(v);
          try {
            localStorage.setItem("hsaFileViewerUseDisplayName", String(v));
          } catch (e) {
            // ignore persistence error
          }
        },
        showAdditionalName,
        showComments,
        showLabel,
        useAdditionalNameInsteadOfFilename,
        openInNewWindow,
        setOpenInNewWindow,
        splitscreenFileIds: [],
        width: props.width,
      }}
    >
      <div className={classes.root} style={{ width: props.width }}>
        <DockLayout
          key={dockKey}
          layout={layout}
          loadTab={loadTab}
          onLayoutChange={onLayoutChange}
          groups={groups}
          style={{
            position: "relative",
            height: "100%",
          }}
        />

        <FilePreviewEditDialog
          open={filePreviewDialogOpen}
          onClose={() => setFilePreviewDialogOpen(false)}
        />

        {/* Sort menu for cases preview tab */}
        <Menu
          anchorEl={anchorElFolder}
          open={Boolean(anchorElFolder)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => onChangeFileSortBy(SortByType.Name)}>
            <span>Name</span>
            {filePreviewSortBy === SortByType.Name && (
              <span>
                {caseFileSortReverse ? <ArrowDownward /> : <ArrowUpward />}
              </span>
            )}
          </MenuItem>
          <MenuItem
            onClick={() => onChangeFileSortBy(SortByType.AdditionalName)}
          >
            <span>Additional Name</span>
            {filePreviewSortBy === SortByType.AdditionalName && (
              <span>
                {caseFileSortReverse ? <ArrowDownward /> : <ArrowUpward />}
              </span>
            )}
          </MenuItem>
          <MenuItem onClick={() => onChangeFileSortBy(SortByType.Date)}>
            <span>Creation Date</span>
            {filePreviewSortBy === SortByType.Date && (
              <span>
                {caseFileSortReverse ? <ArrowDownward /> : <ArrowUpward />}
              </span>
            )}
          </MenuItem>

          <MenuItem onClick={() => onChangeFileSortBy(SortByType.Type)}>
            <span>File Type</span>
            {filePreviewSortBy === SortByType.Type && (
              <span>
                {caseFileSortReverse ? <ArrowDownward /> : <ArrowUpward />}
              </span>
            )}
          </MenuItem>
        </Menu>

        <Menu
          anchorEl={anchorElPreview}
          open={Boolean(anchorElPreview)}
          onClose={handlePreviewMenuClose}
        >
          <MenuItem onClick={openFilePreviewEditDialog}>
            <HSAIcon icon="Edit" size={28} style={{ marginRight: 8 }} />
            <span>Edit Preview</span>
          </MenuItem>
          <MenuItem onClick={toggleAdditionalName}>
            <Checkbox
              size="small"
              checked={showAdditionalName ?? true}
              style={{ padding: 0, marginRight: 8 }}
            />
            <span>Show Additional Name</span>
          </MenuItem>
          <MenuItem onClick={toggleShowComments}>
            <Checkbox
              size="small"
              checked={showComments ?? true}
              style={{ padding: 0, marginRight: 8 }}
            />
            <span>Show Comments</span>
          </MenuItem>
          <MenuItem onClick={toggleShowLabel}>
            <Checkbox
              size="small"
              checked={showLabel ?? true}
              style={{ padding: 0, marginRight: 8 }}
            />
            <span>Show Label</span>
          </MenuItem>
          <MenuItem onClick={toggleUseAdditionalNameInsteadOfFilename}>
            <Checkbox
              size="small"
              checked={useAdditionalNameInsteadOfFilename ?? false}
              style={{ padding: 0, marginRight: 8 }}
            />
            <span>Use Additional Name instead of Filename</span>
          </MenuItem>
          <MenuItem onClick={toggleOpenInNewWindow}>
            <Checkbox
              size="small"
              checked={openInNewWindow ?? false}
              style={{ padding: 0, marginRight: 8 }}
            />
            <span>Open in new Window</span>
          </MenuItem>
        </Menu>
      </div>
    </CaseContext.Provider>
  );
}

CaseTableDockSideBar.propTypes = {
  width: PropTypes.number.isRequired,
  selCase: PropTypes.object,
};

const useStyles = makeStyles({
  root: {
    height: "100%",
    width: 1000,
    background: "#fff",
    overflow: "hidden",
  },
  sortMenuButton: {
    position: "absolute",
    top: 3,
    right: 0,
  },
  previewTabTitle: {
    display: "grid",
    gridTemplateColumns: "1fr auto auto",
    alignItems: "center",
    position: "relative",
    bottom: 6,
    height: 44,
  },
});
